import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AvatarRing from '../../pages/AvatarRing';
import { useUser } from '../../utils/UserContext';
import { getRoleAccent, getRoleLabel } from '../../data/map';
import { MessageBell } from '../MessageCenter';

// ─── Help icon ────────────────────────────────────────────────
const HelpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// ─── Profile Dropdown ─────────────────────────────────────────
const ProfileDropdown = ({ visible, accent, roleLabel, onNavigate }) => (
  <div
    style={{
      position: 'absolute',
      top: 'calc(100% + 10px)',
      left: 0,
      zIndex: 200,
      minWidth: 220,
      pointerEvents: visible ? 'all' : 'none',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.97)',
      transition: 'opacity 0.2s cubic-bezier(.4,0,.2,1), transform 0.2s cubic-bezier(.4,0,.2,1)',
      transformOrigin: 'top left',
    }}
  >
    {/* Card */}
    <div style={{
      position: 'relative',
      background: 'linear-gradient(160deg, rgba(17,24,39,0.99) 0%, rgba(9,18,32,0.99) 100%)',
      border: `1px solid ${accent}45`,
      borderRadius: 16,
      padding: '14px 16px',
      boxShadow: `0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px ${accent}15, 0 0 40px ${accent}10`,
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      overflow: 'hidden',
      backdropFilter: 'blur(20px)',
    }}>

      {/* Subtle top glow strip */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: 1,
        background: `linear-gradient(90deg, transparent, ${accent}80, transparent)`,
      }} />

      {/* Avatar + name + role — AvatarRing derives its own colour/label from the user */}
      <div style={{ padding: '4px 0 14px' }}>
        <AvatarRing
          size={52}
          ringWidth={2}
          compact
          showName={true}
          showRole={true}
          homeRoute={null}
          accent={accent}
        />
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${accent}30, transparent)`, margin: '0 -4px 12px' }} />

      {/* Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0 12px' }}>
        <div style={{ position: 'relative', width: 9, height: 9, flexShrink: 0 }}>
          <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 8px #22c55e' }} />
          {/* Pulse ring */}
          <div style={{
            position: 'absolute',
            inset: -3,
            borderRadius: '50%',
            border: '1.5px solid #22c55e',
            opacity: 0.4,
            animation: 'pulse-ring 2s ease-out infinite',
          }} />
        </div>
        <span style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'system-ui' }}>
          Online
        </span>
        <span style={{
          marginLeft: 'auto',
          fontSize: 10,
          fontWeight: 500,
          color: accent,
          background: `${accent}15`,
          border: `1px solid ${accent}30`,
          borderRadius: 20,
          padding: '1px 8px',
          fontFamily: 'system-ui',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          maxWidth: 140,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }} title={roleLabel}>
          {roleLabel}
        </span>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${accent}30, transparent)`, margin: '0 -4px 10px' }} />

      {/* Go to Profile button */}
      <button
        onClick={onNavigate}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '6px 8px',
          borderRadius: 8,
          color: accent,
          fontSize: 13,
          fontWeight: 500,
          fontFamily: 'system-ui',
          transition: 'background 0.15s ease',
          WebkitTapHighlightColor: 'transparent',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = `${accent}15`; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
      >
        <span>Go to Profile</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </button>
    </div>

    {/* Pulse animation */}
    <style>{`
      @keyframes pulse-ring {
        0%   { transform: scale(1);   opacity: 0.4; }
        70%  { transform: scale(1.8); opacity: 0;   }
        100% { transform: scale(1.8); opacity: 0;   }
      }
    `}</style>
  </div>
);

// ─── TabBar ───────────────────────────────────────────────────
const TabBar = ({
  tabs = [],
  activeTab,
  onTabChange,
  homeRoute = '/profile',
  homeColor,
  onHelpClick,
}) => {
  const navigate  = useNavigate();
  const { user }  = useUser();
  // Group-aware: IGLU orange · Client blue · staff role colour (shared helpers in map.js)
  const accent    = getRoleAccent(user);
  const roleLabel = getRoleLabel(user);

  const tabRefs   = useRef([]);
  const dropRef   = useRef(null);
  const [indicator, setIndicator]   = useState({ left: 0, width: 0 });
  const [dropOpen,  setDropOpen]    = useState(false);

  // ── Measure indicator ──
  const measureTab = useCallback((tabId) => {
    const idx = tabs.findIndex((t) => t.id === tabId);
    if (idx === -1) return;
    const el = tabRefs.current[idx];
    if (!el) return;
    setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
  }, [tabs]);

  useEffect(() => {
    const timer = setTimeout(() => measureTab(activeTab), 100);
    return () => clearTimeout(timer);
  }, [activeTab, measureTab]);

  useEffect(() => {
    const handleResize = () => measureTab(activeTab);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeTab, measureTab]);

  // ── Close dropdown on outside click ──
  useEffect(() => {
    if (!dropOpen) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropOpen]);

  const handleTabClick = (tabId) => {
    onTabChange(tabId);
    requestAnimationFrame(() => measureTab(tabId));
  };

  const handleHelpTap = (e, tab) => {
    e.stopPropagation();
    if (onHelpClick && tab.helpData) {
      onHelpClick(tab.id, tab.helpData);
    }
  };

  const handleProfileNavigate = () => {
    setDropOpen(false);
    navigate(homeRoute);
  };

  return (
    <div className="fr-tab-bar">
      <div className="fr-tab-list" role="tablist">

        {/* ── Avatar replacing home icon ── */}
        <div
          ref={dropRef}
          style={{ position: 'relative', flexShrink: 0 }}
          onClick={() => setDropOpen(prev => !prev)}
        >
          <div style={{ cursor: 'pointer', padding: '4px 6px', display: 'flex', alignItems: 'center' }}>
            <AvatarRing
              size={36}
              ringWidth={2}
              compact
              showName={false}
              showRole={false}
              homeRoute={null}
              accent={accent}
            />
          </div>

          <ProfileDropdown
            visible={dropOpen}
            accent={accent}
            roleLabel={roleLabel}
            onNavigate={handleProfileNavigate}
          />
        </div>

        {/* ── Tabs ── */}
        {tabs.map((tab, idx) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              ref={(el) => (tabRefs.current[idx] = el)}
              role="tab"
              aria-selected={isActive}
              className={`fr-tab-item ${isActive ? 'fr-tab-active' : ''}`}
              onClick={() => handleTabClick(tab.id)}
            >
              {tab.helpData && (
                <span
                  className={`fr-tab-help ${isActive ? 'fr-tab-help-active' : ''}`}
                  onClick={(e) => handleHelpTap(e, tab)}
                  aria-label={`Help for ${tab.label}`}
                >
                  <HelpIcon />
                </span>
              )}
              <span className="fr-tab-label">{tab.label}</span>
            </button>
          );
        })}

        {/* ── Messages bell — right corner, every page, every role ── */}
        <MessageBell accent={accent} label="Global Messages" className="mc-bell-tab" />
      </div>

      {/* ── Sliding gradient underline ── */}
      <div className="fr-tab-track">
        <div
          className="fr-tab-indicator"
          style={{
            left: `${indicator.left}px`,
            width: `${indicator.width}px`,
          }}
        />
      </div>
    </div>
  );
};

export default TabBar;